import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { promotionsApi, leaguesApi, gamesApi } from '../services/api';
import { exportCSV } from '../utils/export';

function Promotions({ user, onLogout }) {
  // List state
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create/Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({ name: '', startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);

  // Detail view state
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoDetail, setPromoDetail] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('games');
  const [detailLoading, setDetailLoading] = useState(false);

  // Add games modal
  const [showAddGames, setShowAddGames] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState(new Set());
  const [gamesLoading, setGamesLoading] = useState(false);

  const leagueIdRef = useRef(user?.league?.id);

  useEffect(() => {
    const init = async () => {
      if (!leagueIdRef.current) {
        try {
          const res = await leaguesApi.getAll();
          const leagues = res.data?.leagues || res.leagues || [];
          if (leagues.length > 0) leagueIdRef.current = leagues[0].id;
        } catch (e) { console.error(e); }
      }
      fetchPromotions();
    };
    init();
  }, []);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const res = await promotionsApi.getAll(leagueIdRef.current);
      const list = res.data?.promotions || res.promotions || [];
      setPromotions(Array.isArray(list) ? list : []);
      setError(null);
    } catch (err) {
      setError('Failed to load promotions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoDetail = async (id) => {
    try {
      setDetailLoading(true);
      const [detailRes, lbRes] = await Promise.all([
        promotionsApi.getById(id),
        promotionsApi.getLeaderboard(id),
      ]);
      setPromoDetail(detailRes.data?.promotion || detailRes.promotion || detailRes.data);
      setLeaderboard(lbRes.data?.leaderboard || lbRes.leaderboard || []);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  // --- CRUD ---
  const handleOpenModal = (promo = null) => {
    setEditingPromo(promo);
    setFormData(promo ? {
      name: promo.name,
      startDate: promo.startDate?.slice(0, 10) || '',
      endDate: promo.endDate?.slice(0, 10) || '',
    } : { name: '', startDate: '', endDate: '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingPromo) {
        await promotionsApi.update(editingPromo.id, formData);
      } else {
        await promotionsApi.create({ ...formData, leagueId: leagueIdRef.current });
      }
      setShowModal(false);
      fetchPromotions();
      if (selectedPromo) fetchPromoDetail(selectedPromo);
    } catch (err) {
      setError(err.message || 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion? This cannot be undone.')) return;
    try {
      await promotionsApi.delete(id);
      if (selectedPromo === id) { setSelectedPromo(null); setPromoDetail(null); }
      fetchPromotions();
    } catch (err) {
      setError(err.message || 'Failed to delete promotion');
    }
  };

  // --- Detail view ---
  const handleSelectPromo = (id) => {
    setSelectedPromo(id);
    setActiveTab('games');
    fetchPromoDetail(id);
  };

  const handleBack = () => {
    setSelectedPromo(null);
    setPromoDetail(null);
    setLeaderboard([]);
  };

  // --- Add games ---
  const handleOpenAddGames = async () => {
    setGamesLoading(true);
    setShowAddGames(true);
    setSelectedGameIds(new Set());
    try {
      const res = await gamesApi.getAll();
      const allGames = res.data?.games || res.games || res.data || [];
      // Filter out games already in this promotion
      const linkedIds = new Set((promoDetail?.games || []).map(g => g.id));
      setAvailableGames(allGames.filter(g => !linkedIds.has(g.id)));
    } catch (err) {
      console.error(err);
    } finally {
      setGamesLoading(false);
    }
  };

  const handleAddSelectedGames = async () => {
    if (selectedGameIds.size === 0) return;
    try {
      await promotionsApi.addGames(selectedPromo, Array.from(selectedGameIds));
      setShowAddGames(false);
      fetchPromoDetail(selectedPromo);
      fetchPromotions();
    } catch (err) {
      setError(err.message || 'Failed to add games');
    }
  };

  const handleRemoveGame = async (gameId) => {
    try {
      await promotionsApi.removeGame(selectedPromo, gameId);
      fetchPromoDetail(selectedPromo);
      fetchPromotions();
    } catch (err) {
      setError(err.message || 'Failed to remove game');
    }
  };

  const toggleGameSelection = (gameId) => {
    setSelectedGameIds(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId); else next.add(gameId);
      return next;
    });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-';

  // --- DETAIL VIEW ---
  if (selectedPromo && promoDetail) {
    return (
      <div className="admin-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="main-content">
          <div className="page-header">
            <div>
              <button className="btn btn-secondary" onClick={handleBack} style={{ marginBottom: 8 }}>&larr; Back</button>
              <h2>{promoDetail.name}</h2>
              <p>{formatDate(promoDetail.startDate)} &mdash; {formatDate(promoDetail.endDate)} &middot; {promoDetail.leagueName}</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => handleOpenModal(promoDetail)}>Edit</button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
            <button
              className={`btn ${activeTab === 'games' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('games')}
            >Games ({promoDetail.games?.length || 0})</button>
            <button
              className={`btn ${activeTab === 'leaderboard' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setActiveTab('leaderboard'); fetchPromoDetail(selectedPromo); }}
            >Leaderboard</button>
          </div>

          {/* Games Tab */}
          {activeTab === 'games' && (
            <div className="card">
              <div className="card-header">
                <h3>Linked Games</h3>
                <button className="btn btn-primary" onClick={handleOpenAddGames}>+ Add Games</button>
              </div>

              {detailLoading ? (
                <div className="loading"><div className="loading-spinner"></div></div>
              ) : (promoDetail.games || []).length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🎮</div>
                  <h3>No games linked</h3>
                  <p>Add games to this promotion to start tracking standings.</p>
                  <button className="btn btn-primary" onClick={handleOpenAddGames}>Add Games</button>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Venue</th>
                      <th>Type</th>
                      <th>Players</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoDetail.games.map(game => (
                      <tr key={game.id}>
                        <td>{formatDateTime(game.startTime)}</td>
                        <td>{game.venueName || '-'}</td>
                        <td>{game.gameType}</td>
                        <td>{game.seatedCount}/{game.maxPlaces}</td>
                        <td>
                          <span className={`badge ${game.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                            {game.status || 'active'}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-danger btn-sm" onClick={() => handleRemoveGame(game.id)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div className="card">
              <div className="card-header">
                <h3>Standings</h3>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: '#888' }}>{leaderboard.length} players</span>
                  {leaderboard.length > 0 && (
                    <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(
                      `${promoDetail.name}-leaderboard.csv`,
                      ['Rank', 'First Name', 'Last Name', 'Points', 'Games Played', 'Best Finish'],
                      leaderboard.map(p => [p.rank, p.firstName, p.lastName, p.totalPoints, p.gamesPlayed, p.bestFinish])
                    )}>Export CSV</button>
                  )}
                </div>
              </div>

              {detailLoading ? (
                <div className="loading"><div className="loading-spinner"></div></div>
              ) : leaderboard.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">🏆</div>
                  <h3>No results yet</h3>
                  <p>Standings will appear here as games are completed.</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Player</th>
                      <th>Points</th>
                      <th>Games</th>
                      <th>Best Finish</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map(player => (
                      <tr key={player.playerId}>
                        <td>
                          <strong>{player.rank <= 3 ? ['🥇','🥈','🥉'][player.rank - 1] : player.rank}</strong>
                        </td>
                        <td><strong>{player.firstName} {player.lastName}</strong></td>
                        <td><strong>{player.totalPoints}</strong></td>
                        <td>{player.gamesPlayed}</td>
                        <td>{player.bestFinish === 1 ? '1st' : player.bestFinish === 2 ? '2nd' : player.bestFinish === 3 ? '3rd' : `${player.bestFinish}th`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Add Games Modal */}
          {showAddGames && (
            <div className="modal-overlay" onClick={() => setShowAddGames(false)}>
              <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
                <div className="modal-header">
                  <h3>Add Games to {promoDetail.name}</h3>
                  <button className="modal-close" onClick={() => setShowAddGames(false)}>&times;</button>
                </div>
                <div className="modal-body" style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {gamesLoading ? (
                    <div className="loading"><div className="loading-spinner"></div></div>
                  ) : availableGames.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#888' }}>No available games to add.</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              checked={selectedGameIds.size === availableGames.length && availableGames.length > 0}
                              onChange={() => {
                                if (selectedGameIds.size === availableGames.length) {
                                  setSelectedGameIds(new Set());
                                } else {
                                  setSelectedGameIds(new Set(availableGames.map(g => g.id)));
                                }
                              }}
                            />
                          </th>
                          <th>Date</th>
                          <th>Venue</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableGames.map(game => (
                          <tr key={game.id} onClick={() => toggleGameSelection(game.id)} style={{ cursor: 'pointer' }}>
                            <td>
                              <input type="checkbox" checked={selectedGameIds.has(game.id)} readOnly />
                            </td>
                            <td>{formatDateTime(game.startTime || game.start_time)}</td>
                            <td>{game.venueName || game.venue_name || game.venue?.name || '-'}</td>
                            <td>{game.gameType || game.game_type || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowAddGames(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleAddSelectedGames} disabled={selectedGameIds.size === 0}>
                    Add {selectedGameIds.size} Game{selectedGameIds.size !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit modal */}
          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingPromo ? 'Edit Promotion' : 'Create Promotion'}</h3>
                  <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Name *</label>
                      <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input type="date" value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>End Date *</label>
                      <input type="date" value={formData.endDate} onChange={e => setFormData({ ...formData, endDate: e.target.value })} required />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editingPromo ? 'Save Changes' : 'Create'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // --- LIST VIEW ---
  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <div>
            <h2>Promotions</h2>
            <p>Manage tournament series for your league</p>
          </div>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>+ Create Promotion</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card">
          <div className="card-header">
            <h3>All Promotions</h3>
          </div>

          {loading ? (
            <div className="loading"><div className="loading-spinner"></div></div>
          ) : promotions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🏆</div>
              <h3>No promotions yet</h3>
              <p>Create your first tournament series to track standings across multiple games.</p>
              <button className="btn btn-primary" onClick={() => handleOpenModal()}>Create Promotion</button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Dates</th>
                  <th>Games</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map(promo => (
                  <tr key={promo.id} onClick={() => handleSelectPromo(promo.id)} style={{ cursor: 'pointer' }}>
                    <td><strong>{promo.name}</strong></td>
                    <td>{formatDate(promo.startDate)} &mdash; {formatDate(promo.endDate)}</td>
                    <td>{promo.gameCount} game{promo.gameCount !== 1 ? 's' : ''}</td>
                    <td>
                      <span className={`badge ${promo.isActive ? 'badge-success' : 'badge-warning'}`}>
                        {promo.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); handleOpenModal(promo); }}>Edit</button>
                      {' '}
                      <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleDelete(promo.id); }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingPromo ? 'Edit Promotion' : 'Create Promotion'}</h3>
                <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Spring Championship" required />
                  </div>
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input type="date" value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>End Date *</label>
                    <input type="date" value={formData.endDate} onChange={e => setFormData({ ...formData, endDate: e.target.value })} required />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : editingPromo ? 'Save Changes' : 'Create Promotion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Promotions;
